/**
 * Change visibility (FSD Part D, XFN-UX-015).
 *
 * > A user returning to a record SHALL be able to see what has changed since
 * > they last acted on it, and by whom.
 *
 * The build already writes the complete answer: `audit_log` carries every
 * mutation with actor, timestamp and a field-level `before`/`after` diff (§5),
 * and `GET /api/v1/audit/timeline` returns it per record. What has never
 * existed is the *baseline* — "since **they** last acted" — so the timeline
 * shows a hundred entries and the user reads all of them to find the two that
 * happened after they left.
 *
 * The baseline does not need new server state. The viewer's own last action is
 * in the timeline: it is the most recent entry whose `actorId` is theirs, and
 * whose action is an act rather than a look. Deriving it from the audit log,
 * instead of storing a per-user "last viewed" marker, is both cheaper and
 * stricter — an opened tab is not an act, and a marker written on render would
 * quietly declare the changes seen before the user had read them.
 *
 * Two honest limits, neither of them papered over:
 *
 *  - `TimelineQuery` has `entity`, `entityId` and `limit` — **no `since`** — so
 *    a caller fetching the last N entries can have the viewer's own last action
 *    fall off the end of the window. {@link changesSince} therefore takes
 *    `windowComplete` and answers `baseline: 'unknown'` rather than
 *    `'none'` when it cannot tell the difference between "you have never acted
 *    on this" and "your action is older than what I fetched".
 *  - A field-level diff is only as good as what the service recorded in
 *    `before`/`after`; sensitive values are masked there (§5) and stay masked
 *    here.
 */

/**
 * One audit entry, structurally satisfied by the generated `LogEntry` DTO. The
 * shared library does not import the generated client (it is presentational and
 * API-agnostic — see `index.ts`), so the shape is stated rather than imported;
 * `changes.test.ts` asserts a `LogEntry` is assignable to it, which is what
 * keeps the two from drifting.
 */
export interface ChangeEntry {
  id: number | string
  /** ISO 8601 instant, as the server emits it. */
  at: string
  action: string
  actorId?: string | undefined
  actorType?: string | undefined
  /** Display name, when the app has resolved one. */
  actorLabel?: string | undefined
  before?: Record<string, unknown> | undefined
  after?: Record<string, unknown> | undefined
  note?: string | undefined
}

/**
 * Actions that are looks, not acts. A user who opened a record, printed it or
 * revealed a bank field has not acted on it, so none of these move the
 * baseline — otherwise printing a purchase order would mark every change made
 * while you were away as already seen.
 */
export const PASSIVE_ACTIONS: ReadonlySet<string> = new Set([
  'export',
  'print',
  'sensitive_reveal',
  'login_success',
  'login_failure',
  'logout',
  'session_revoke',
  'support_session_start',
  'support_session_end',
])

export function isActingAction(action: string): boolean {
  return !PASSIVE_ACTIONS.has(action)
}

/** How the "since" line was established — rendered wording depends on it. */
export type ChangeBaseline = 'last-action' | 'explicit' | 'none' | 'unknown'

export interface FieldChange {
  field: string
  /** Value as it stood at the baseline. */
  from: unknown
  /** Value now. */
  to: unknown
  /** Actor ids that touched this field in the interval, most recent first. */
  actorIds: string[]
  /** Instant of the most recent change to this field. */
  at: string
}

export interface ChangeActor {
  id: string
  label?: string | undefined
  /** Most recent action by this actor in the interval. */
  at: string
  count: number
}

export interface ChangesSince {
  baseline: ChangeBaseline
  /** The viewer's own last act, when there is one in the window. */
  markerAt?: string | undefined
  /** Entries strictly after the baseline, most recent first. */
  entries: ChangeEntry[]
  /** Net field-level movement across the interval. */
  fields: FieldChange[]
  actors: ChangeActor[]
  /** Convenience: `entries.length`, which is what a badge shows. */
  count: number
}

export interface ChangesSinceOptions {
  /** The current user's audit actor id, as a string (`LogEntry.actorId`). */
  viewerId?: string | undefined
  /**
   * An explicit baseline instant, when the caller has a better one than the
   * audit log — for example the moment the user submitted the form now on
   * screen. Wins over the derived marker.
   */
  since?: string | undefined
  /**
   * True when the entries passed in are the record's whole history. False (the
   * default) when they are a `limit`-capped window, which is what
   * `auditLogTimeline` returns.
   */
  windowComplete?: boolean | undefined
}

function time(value: string): number {
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

function byNewestFirst(a: ChangeEntry, b: ChangeEntry): number {
  return time(b.at) - time(a.at)
}

/** Structural equality good enough for audit scalars and small JSON values. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return a == null && b == null
  if (typeof a !== 'object' || typeof b !== 'object') return false
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

/**
 * The viewer's last act on this record, or `null`. Passive actions are ignored
 * (see {@link PASSIVE_ACTIONS}).
 */
export function lastActionBy(
  entries: readonly ChangeEntry[],
  viewerId: string | undefined,
): ChangeEntry | null {
  if (viewerId === undefined || viewerId === '') return null
  const mine = entries.filter(
    (entry) => entry.actorId === viewerId && isActingAction(entry.action),
  )
  if (mine.length === 0) return null
  return [...mine].sort(byNewestFirst)[0] ?? null
}

/**
 * What changed since the viewer last acted, and by whom.
 *
 * Entries are returned newest first; `fields` collapses the interval into net
 * movement per field — a value changed three times reads as one row from its
 * value at the baseline to its value now, and a value changed and changed back
 * does not appear at all, because nothing about the record is different.
 */
export function changesSince(
  entries: readonly ChangeEntry[],
  options: ChangesSinceOptions = {},
): ChangesSince {
  const ordered = [...entries].sort(byNewestFirst)

  const marker = options.since !== undefined ? null : lastActionBy(ordered, options.viewerId)
  const markerAt = options.since ?? marker?.at
  const baseline: ChangeBaseline =
    options.since !== undefined
      ? 'explicit'
      : marker !== null
        ? 'last-action'
        : options.windowComplete === true
          ? 'none'
          : 'unknown'

  if (markerAt === undefined) {
    // No baseline: nothing can honestly be called new. Saying "everything is
    // new" to a user who has never opened the record would be noise, and saying
    // it to one whose action fell outside the fetched window would be a lie.
    return { baseline, entries: [], fields: [], actors: [], count: 0 }
  }

  const cutoff = time(markerAt)
  const since = ordered.filter((entry) => {
    if (time(entry.at) <= cutoff) return false
    // The viewer's own later entries are not news to them, but a change they
    // made from another session still counts as a change to the record, so only
    // the marker entry itself is excluded — by the strict cutoff above.
    return true
  })

  /* --------------------------------------------------- net field movement */

  // Walked oldest-first so `from` is the earliest recorded prior value and `to`
  // the latest; the entry list itself stays newest-first for display.
  const oldestFirst = [...since].reverse()
  const fields = new Map<string, FieldChange>()

  for (const entry of oldestFirst) {
    const touched = new Set([
      ...Object.keys(entry.before ?? {}),
      ...Object.keys(entry.after ?? {}),
    ])
    for (const field of touched) {
      const from = entry.before?.[field]
      const to = entry.after?.[field]
      const existing = fields.get(field)
      if (existing) {
        existing.to = to
        existing.at = entry.at
        if (entry.actorId !== undefined && !existing.actorIds.includes(entry.actorId)) {
          existing.actorIds.unshift(entry.actorId)
        }
      } else {
        fields.set(field, {
          field,
          from,
          to,
          actorIds: entry.actorId === undefined ? [] : [entry.actorId],
          at: entry.at,
        })
      }
    }
  }

  const netFields = [...fields.values()]
    .filter((change) => !sameValue(change.from, change.to))
    .sort((a, b) => time(b.at) - time(a.at))

  /* ----------------------------------------------------------- who by whom */

  const actors = new Map<string, ChangeActor>()
  for (const entry of since) {
    const id = entry.actorId ?? entry.actorType ?? 'system'
    const existing = actors.get(id)
    if (existing) {
      existing.count += 1
      if (time(entry.at) > time(existing.at)) existing.at = entry.at
      existing.label ??= entry.actorLabel
    } else {
      actors.set(id, { id, label: entry.actorLabel, at: entry.at, count: 1 })
    }
  }

  return {
    baseline,
    markerAt,
    entries: since,
    fields: netFields,
    actors: [...actors.values()].sort((a, b) => time(b.at) - time(a.at)),
    count: since.length,
  }
}
